/* eslint-disable no-useless-escape */
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import {
  Row,
  Col,
} from 'antd';
import SimpleReactValidator from 'simple-react-validator';
import {
  validators,
} from 'utils/validationMessages';

import { API_END_POINTS } from 'api';
import { MESSAGES } from 'appconstants';

import {
  SpForm,
  SpError,
  SpTextMute,
  SpText,
} from 'components/DesignKit';

import KYCFileUpload from 'components/KycFileUpload';
import { CheckCircleOutlined } from '@ant-design/icons';

type Props = {
  isIndia: boolean,
}

const UploadDocumentDetails = (props: Props, ref) => {
  const { isIndia } = props;

  const [, forceUpdate] = useState();
  const [bankStatementUrl, setBankStatementUrl] = useState('');
  const [voidCheckUrl, setVoidCheckUrl] = useState('');
  const [driverLicenseUrl, setDriverLicenseUrl] = useState('');
  const accountRegex = /[0-9]{6,17}/;
  const routingRegex = /[0-9]{9}/;
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const averagemonthlyVolumeHandler = useRef();
  const [successFiles, setSuccessFiles] = useState([]);

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      confirmAccount: {
        message: MESSAGES.BANK_DETAILS.MATCH,
        rule: (val, params) => val === params[0],
      },
      accountNumber: {
        message: MESSAGES.BANK_DETAILS.VALID,
        rule: val => accountRegex.test(val),
      },
      bankIFSC: {
        message: MESSAGES.BANK_DETAILS.IFSC,
        rule: val => ifscRegex.test(val),
      },
      bankRoutingNumber: {
        message: MESSAGES.BANK_DETAILS.ROUTING,
        rule: val => routingRegex.test(val),
      },
      bankNameValidation: {
        message: validators.bussiness.validBankName,
        rule: val => /^[A-Za-z0-9&,\s.\-'\$@_]{1,50}$/.test(val),
      },
      accountName: {
        message: validators.bussiness.validBankAccountName,
        // eslint-disable-next-line no-useless-escape
        rule: val => /^[A-Za-z0-9&,\s.\-'\$@_]{1,50}$/.test(val),
      },
      averageTicketAmountCheck: {
        message: 'Average ticket amount should be less than average monthly amount',
        rule: (val) => Number(averagemonthlyVolumeHandler?.current?.input?.value.replaceAll(',', '')) > Number(val ? val.replaceAll(',', '') : 0.00),
      },

    },
  }));

  simpleValidator.current.purgeFields();

  useImperativeHandle(ref, () => ({
    validate: () => {
      const formValid = simpleValidator.current.allValid();
      if (!formValid) {
        simpleValidator.current.showMessages();
        forceUpdate(1);
        return false;
      }
      return true;
    },
    getValues: () => ({
      bankStatementUrl,
      voidCheckUrl,
      driverLicenseUrl,
    }),
  }));

  const addSuccessFile = (fileName) => {
    setSuccessFiles(prev => (
      prev.includes(fileName) ? prev : [...prev, fileName]
    ));
  };


  return (
    <>
      <Row className="my-2 mb-3">
        <Col>
          <SpText fontSize="20px" fontWeight="600">
            Document Upload
          </SpText>
        </Col>
      </Row>
      <SpForm>

        {
          !isIndia && (
            <>
              <Row type="flex" justify="start" align="middle">
                <Col span={8}>
                  <span>Void Check or Bank Letter/Statement</span>
                </Col>
                <Col span={16}>
                  <KYCFileUpload
                    name="Void Check"
                    endPoint={API_END_POINTS.US_BUSINESS_VOID_CHECK}
                    url={voidCheckUrl}
                    keyType="US_BUSINESS_VOID_CHECK"
                    setUrl={value => { setVoidCheckUrl(value); if (value) addSuccessFile('Void Check'); }}
                    type="Business"
                  />
                  <SpError>
                    {simpleValidator.current.message('Void Check', voidCheckUrl, 'required')}
                  </SpError>
                </Col>
                <Col span={8} className="ml-4" />
              </Row>
            </>
          )
        }

        {
          isIndia && (
            <>
              <Row type="flex" justify="start" align="middle">
                <Col span={8}>
                  <span>Bank Account Statement</span>
                </Col>
                <Col span={8}>
                  <KYCFileUpload
                    name="Bank Statement"
                    endPoint={API_END_POINTS.UPLOAD_BANK_STATEMENT}
                    url={bankStatementUrl}
                    setUrl={value => { setBankStatementUrl(value); if (value) addSuccessFile('Bank Statement'); }}
                    type="Business"
                  />
                  <SpError>
                    {simpleValidator.current.message('Bank statement proof', bankStatementUrl, 'required')}
                  </SpError>
                </Col>
                <Col span={6} className="ml-2">
                  <SpTextMute>
                    Upload an Image or pdf with bank account number, IFSC code,
                    company name all in one document. Self attested mandatory.
                  </SpTextMute>
                </Col>
              </Row>
            </>
          )
        }
        {!isIndia && (
        <Row type="flex" justify="start" align="middle">
          <Col span={8}>
            <span>Govt. ID (Driver License, Passport etc.)</span>
          </Col>
          <Col span={8}>
            <KYCFileUpload
              name="Driver License"
              endPoint={API_END_POINTS.US_BUSINESS_GOVERNMENT_ID}
              url={driverLicenseUrl}
              keyType="US_BUSINESS_GOVERNMENT_ID"
              setUrl={value => { setDriverLicenseUrl(value); if (value) addSuccessFile('Driver License'); }}
              type="Business"
            />
            <SpError>
              {simpleValidator.current.message('govt id url', driverLicenseUrl, 'required')}
            </SpError>
          </Col>
          <Col span={6} className="ml-2" />
        </Row>
        )}
        {successFiles.map((item) => (
          <Row
            type="flex"
            justify="space-between"
            align="middle"
            style={{
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '10px',
              boxShadow: '0 0 0 1px #e0e0e0',
              maxWidth: '360px',
            }}
          >
            <Col>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#000000' }}>
                {item}
              </div>
              <div style={{ color: '#007ED4', fontSize: '13px', marginTop: '4px' }}>
                Upload Complete
              </div>
            </Col>
            <Col>
              <CheckCircleOutlined style={{ color: '#00E38C', fontSize: '28px' }} />
            </Col>
          </Row>
        ))}
      </SpForm>
    </>
  );
};

// $FlowFixMe
export default forwardRef(UploadDocumentDetails);
